import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  LayoutGrid,
  Info,
  Monitor,
  Smartphone,
  Loader2,
  Lock,
  Send,
  Eye,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useEmailSequences, TemplatePreviewResult } from '../../hooks/useEmailSequences';
import { useAuth } from '../../hooks/useAuth';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// Same role gate as LeadCaptureEmailPage. Brokers and their support roles get
// a read-only gallery so they can see the brand surface their leads receive.
const MANAGER_ROLES = new Set<string>([
  'super_admin',
  'administrator',
  'office_admin',
  'marketing',
]);

// ---------- Template card ----------
//
// Cards are derived from the backend registry. The previous version kept a
// second, hand-maintained array of 12 cards, each carrying a hardcoded
// `lastSent` ("Today", "2 hrs ago", "1 week ago") and `openRate` ("62%",
// "44%") that no code ever computed. Every tenant saw the same invented
// numbers, including tenants that had never sent an email. Nothing on a card
// is a metric now: a card states what the template is and whether it can be
// sent, both of which are facts.

interface CardProps {
  templateId: string;
  label: string;
  sendable: boolean;
  mergeFields: string[];
  previewOnlyFields: string[];
  selected: boolean;
  onClick: () => void;
}

/** A deterministic, brand-neutral swatch so the grid stays scannable. */
function swatchFor(templateId: string): string {
  const palette = [
    '#2563eb',
    '#0891b2',
    '#7c3aed',
    '#0d9488',
    '#c2410c',
    '#4f46e5',
    '#be123c',
    '#15803d',
  ];
  let hash = 0;
  for (let i = 0; i < templateId.length; i += 1) {
    hash = (hash * 31 + templateId.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

function initialsFor(label: string): string {
  return label
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

const TemplateCard: React.FC<CardProps> = ({
  templateId,
  label,
  sendable,
  mergeFields,
  previewOnlyFields,
  selected,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-lg border p-4 transition-colors ${
      selected
        ? 'border-blue-500/50 bg-blue-500/5'
        : 'border-gray-700 bg-gray-800/40 hover:border-gray-500'
    }`}
  >
    <div className="flex items-start gap-3 mb-2">
      <div
        className="w-10 h-10 rounded font-bold text-white text-xs inline-flex items-center justify-center flex-shrink-0"
        style={{ background: swatchFor(templateId) }}
      >
        {initialsFor(label)}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-white truncate">{label}</h4>
        <p className="text-[10px] text-gray-500 mt-0.5 font-mono truncate">
          {templateId}
        </p>
      </div>
    </div>
    <p className="text-xs text-gray-400 leading-relaxed">
      {sendable
        ? `Personalised from ${mergeFields.length} lead and broker ${
            mergeFields.length === 1 ? 'field' : 'fields'
          }.`
        : `Needs ${previewOnlyFields.slice(0, 3).join(', ')}${
            previewOnlyFields.length > 3 ? ' and more' : ''
          }, which we cannot read from a lead record.`}
    </p>
    <div className="flex items-center justify-between mt-3 text-[11px]">
      {sendable ? (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-semibold">
          <Send className="w-2.5 h-2.5" />
          Can be sent
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 font-semibold">
          <Eye className="w-2.5 h-2.5" />
          Preview only
        </span>
      )}
    </div>
  </button>
);

// ---------- Main page ----------

const LeadCaptureTemplatesPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  const { renderTemplatePreview, templates, templatesLoading } =
    useEmailSequences(companyId);
  const { claims, isFounder } = useAuth();
  const role = claims?.role ?? userData?.role ?? '';
  const canManage = isFounder || MANAGER_ROLES.has(role);

  const [selectedId, setSelectedId] = useState<string>('');
  const [desktopPreview, setDesktopPreview] = useState<boolean>(true);
  const [serverPreview, setServerPreview] = useState<TemplatePreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Sendable templates first: what a tenant can actually use should lead.
  const ordered = useMemo(
    () =>
      [...templates].sort((a, b) =>
        a.sendable === b.sendable ? a.label.localeCompare(b.label) : a.sendable ? -1 : 1,
      ),
    [templates],
  );
  const selected = ordered.find((t) => t.templateId === selectedId) ?? ordered[0] ?? null;
  const sendableCount = templates.filter((t) => t.sendable).length;

  useEffect(() => {
    if (!selectedId && ordered.length > 0) setSelectedId(ordered[0].templateId);
  }, [ordered, selectedId]);

  useEffect(() => {
    if (!selected || !companyId) {
      setServerPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    renderTemplatePreview({ templateId: selected.templateId })
      .then((r) => {
        if (!cancelled) setServerPreview(r);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewError(err instanceof Error ? err.message : String(err));
          setServerPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, companyId, renderTemplatePreview]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutGrid}
        title="Email Template Gallery"
        subtitle="The nurture templates VALUTTEN can send, previewed with your own business name"
      />

      {!canManage && (
        <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-blue-100">View-only access</div>
            <div className="text-blue-300/90 mt-0.5">
              Email templates are managed by your team's admin or marketing
              role. Browse the gallery to see what your leads receive.
            </div>
          </div>
        </div>
      )}

      {/* What this page is, stated plainly. The previous copy told tenants to
          "use the editor here for text/image swaps", and there is no editor. */}
      <div className="rounded-lg border-l-4 border-l-blue-500 border-t border-r border-b border-gray-700 bg-gray-800/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">
            About these templates
          </h3>
        </div>
        <ul className="space-y-1.5 text-xs text-gray-400">
          <li className="flex gap-2">
            <span className="text-gray-600 flex-shrink-0">•</span>
            <span>
              This is a read-only gallery. Editing template copy or imagery in
              the app is not built yet, so changes are made by the VALUTTEN team
              on request. There is no editor on this page.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-gray-600 flex-shrink-0">•</span>
            <span>
              <strong className="text-gray-300">Can be sent</strong> means every
              piece of information the template needs can be read from the lead
              record, the assigned broker and your saved branding.{' '}
              <strong className="text-gray-300">Preview only</strong> means it
              references loan details such as the lender, interest rate or
              dollar amounts, which we cannot read from a lead. Those templates
              are blocked from sending rather than filled in with sample values.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-gray-600 flex-shrink-0">•</span>
            <span>
              Previews use neutral sample data and your own business name. Your
              logo, brand colour, ABN, address and phone from the Setup Wizard
              are applied to real sends.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-gray-600 flex-shrink-0">•</span>
            <span>
              Logos and images are stored in Firebase Storage under your company,
              uploaded once in the Setup Wizard and served from there.
            </span>
          </li>
        </ul>
      </div>

      {templatesLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="rounded-lg border border-gray-700 bg-gray-800/40 p-4 animate-pulse"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded bg-gray-700 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-700 rounded w-2/3" />
                  <div className="h-2 bg-gray-700/60 rounded w-1/3" />
                </div>
              </div>
              <div className="h-2 bg-gray-700/60 rounded w-full mb-1.5" />
              <div className="h-2 bg-gray-700/60 rounded w-4/5" />
            </div>
          ))}
        </div>
      )}

      {!templatesLoading && templates.length === 0 && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <LayoutGrid className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-white mb-1">
            No templates available
          </h3>
          <p className="text-xs text-gray-400">
            The template registry could not be loaded. Refresh the page, and
            contact support if it keeps happening.
          </p>
        </div>
      )}

      {!templatesLoading && templates.length > 0 && (
        <>
          <div className="text-xs text-gray-500">
            {sendableCount} of {templates.length} templates can be sent from
            lead data today.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {ordered.map((tpl) => (
              <TemplateCard
                key={tpl.templateId}
                templateId={tpl.templateId}
                label={tpl.label}
                sendable={tpl.sendable}
                mergeFields={tpl.mergeFields}
                previewOnlyFields={tpl.previewOnlyFields}
                selected={tpl.templateId === selected?.templateId}
                onClick={() => setSelectedId(tpl.templateId)}
              />
            ))}
          </div>
        </>
      )}

      {/* Preview pane */}
      {selected && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-white">
                Preview: {selected.label}
                {!selected.sendable && (
                  <span className="ml-2 text-[10px] font-normal text-amber-400">
                    (preview only)
                  </span>
                )}
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Sample data, your branding
                {serverPreview && serverPreview.availableMergeFields.length > 0 && (
                  <>
                    {' '}· merge fields:{' '}
                    {serverPreview.availableMergeFields.map((k, i) => (
                      <React.Fragment key={k}>
                        {i > 0 && ', '}
                        <code className="font-mono text-gray-400">{`{{${k}}}`}</code>
                      </React.Fragment>
                    ))}
                  </>
                )}
              </p>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => setDesktopPreview(false)}
                className={`px-2.5 py-1 rounded text-[11px] inline-flex items-center gap-1 ${
                  !desktopPreview
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:text-white'
                }`}
              >
                <Smartphone className="w-3 h-3" />
                Mobile
              </button>
              <button
                type="button"
                onClick={() => setDesktopPreview(true)}
                className={`px-2.5 py-1 rounded text-[11px] inline-flex items-center gap-1 ${
                  desktopPreview
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:text-white'
                }`}
              >
                <Monitor className="w-3 h-3" />
                Desktop
              </button>
            </div>
          </div>

          {!selected.sendable && (
            <div className="px-5 py-2.5 bg-amber-500/5 border-b border-amber-500/20 text-[11px] text-amber-200">
              This template cannot be sent yet. It needs{' '}
              {selected.previewOnlyFields.join(', ')}, and there is no real
              source for those on a lead record. Sending is blocked rather than
              filled in with sample values, so it is not offered when building a
              sequence.
            </div>
          )}

          <div className="p-6 bg-gray-900/60">
            {previewLoading && (
              <div className="mx-auto bg-gray-800/40 rounded-md border border-gray-700 animate-pulse"
                   style={{ maxWidth: desktopPreview ? 600 : 360, height: 420 }} />
            )}
            {previewError && !previewLoading && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
                {previewError}
              </div>
            )}
            {!previewLoading && !previewError && serverPreview && (
              <div
                className="mx-auto bg-white rounded-md shadow-lg overflow-hidden"
                style={{ maxWidth: desktopPreview ? 600 : 360 }}
              >
                <div className="px-4 py-2 border-b border-gray-200 text-xs text-gray-600">
                  <strong>Subject:</strong> {serverPreview.subject}
                </div>
                {/* Sandboxed iframe so nothing in a template body can reach the
                    parent page. */}
                <iframe
                  title={`Email preview: ${selected.label}`}
                  sandbox=""
                  srcDoc={serverPreview.html}
                  className="w-full border-0"
                  style={{ minHeight: 420 }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadCaptureTemplatesPage;
